import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import SETTINGS from '../settings';

const url = (key, frmt, type, opts = '') => {
  let path = key;
  if (type != null) {
    const arr = path.split('/');
    arr.splice(arr.length - 1, 0, type);

    path = arr.join('/');
  }

  return `${SETTINGS.CLOUDINARY_ROOT_URL}/${path}${opts}.${frmt}`;
};

export const getCloudinaryUrl = ({ imageKey, prefix = '', type, format = 'webp' }) => {
  if (!imageKey) return null;
  return url(`${prefix}${imageKey}`, format, type);
};

// Width ladders rendered on the CDN per top-level folder (parabo-product-images spec.json).
// wNNN variants exist only for these folders, webp only, DPR 1 only — the ladder itself covers
// density. Passing a width outside the folder's ladder produces a 404.
export const WIDTH_LADDERS = {
  carousel: [480, 640, 960, 1200, 1600],
  categories: [320, 480, 640, 960],
  products: [320, 480, 640],
  instagram: [160, 240, 320, 480],
  landing: [320, 480, 640, 960, 1280],
  screens: [320, 480, 640],
};

// Width-descriptor srcset for folders with a wNNN ladder, e.g.
// "…/categories/w320/cards.webp 320w, …/categories/w480/cards.webp 480w, …".
// Shared by CloudinaryPicture and the hero <link rel="preload"> so the two can never drift.
export const getCloudinarySrcSet = ({ imageKey, prefix = '', widths, format = 'webp' }) => {
  if (!imageKey || !widths?.length) return null;
  return widths.map(w => `${url(`${prefix}${imageKey}`, format, `w${w}`)} ${w}w`).join(', ');
};

// DPR-pair srcset ("<1x>, <@2x> 2x") — the default webp <source>. Exported so a hero preload
// can mirror the rendered candidate list exactly for folders without a width ladder.
export const getCloudinaryDprSrcSet = ({ imageKey, prefix = '', type, format = 'webp' }) => {
  if (!imageKey) return null;
  const key = `${prefix}${imageKey}`;
  return `${url(key, format, type)}, ${url(key, format, type, '@2x')} 2x`;
};

const CloudinaryPicture = props => {
  // Explicit `alt` wins (incl. "" to mark a decorative image); otherwise fall back
  // to the generated alt catalog. SKU thumbnails (prefix "sku/") aren't in the
  // catalog, so they rely on the caller passing a meaningful `alt`.
  const altText = props.alt ?? SETTINGS.CLOUDINARY_ALT_TEXTS[props.imageKey] ?? '';
  // there is difference between props.imageKey and real imageKey
  // it's due to images.json generator
  // TODO don't remove prefix in images.json generator, and then we don't need any prefix and differences
  const imageKey = `${props.prefix}${props.imageKey}`;

  if (props.assetType === 'video') {
    // `type` is optional, but the video branch needs it to resolve a generated poster (big |
    // small only — there is no default-variant poster). Default to big (the autoplaying main
    // rendering) so an unspecified caller isn't left with a 404 poster and an idle blank box.
    const variant = props.type ?? 'big';
    return (
      <video
        ref={props.videoRef}
        className={classNames(props.className, "w-100")}
        style={props.style}
        // <video> has no `alt`; expose the authored text as the accessible name.
        // Omit when empty (decorative thumbnails pass alt="") to avoid a blank label.
        aria-label={altText || undefined}
        title={altText || undefined}
        poster={url(imageKey, 'jpg', variant)}
        autoPlay={variant === "big"}
        // Known limitation: autoplay/preload are keyed on the variant, not slide visibility, and
        // pure-react-carousel mounts every slide — so a big-variant video still autoplays/preloads
        // (fetches) while off-screen. The product carousel pauses its interval on a video slide and
        // advances only when the active video ends (see productDetailCarousel), but off-screen
        // preload stays un-gated. Revisit with IntersectionObserver if galleries gain multiple or
        // non-leading videos. Thumbnails (small) already stay idle.
        preload={variant === "big" ? "auto" : "none"}
        loop={props.loop}
        muted
        playsInline
      >
        <source src={url(imageKey, 'webm')} type="video/webm" />
        <source src={url(imageKey, 'mp4')} type="video/mp4" />
      </video>
    );
  }


  // Width-descriptor srcset is opt-in per call site: only folders listed in WIDTH_LADDERS have
  // wNNN variants on the CDN, everything else (sku/, team/, …) must keep the DPR pair.
  const widthSrcSet = props.widths
    ? getCloudinarySrcSet({ imageKey: props.imageKey, prefix: props.prefix, widths: props.widths })
    : null;

  if (process.env.NODE_ENV !== 'production') {
    if (props.widths && props.type) {
      // The wNNN directory occupies the same inserted path segment as `type`.
      console.warn(`CloudinaryPicture: widths and type are mutually exclusive (${imageKey})`);
    }
    if (!!props.widths !== !!props.sizes) {
      console.warn(`CloudinaryPicture: widths and sizes must be passed together (${imageKey})`);
    }
  }

  return (
    <picture>
      <source
        type="image/webp"
        srcSet={
          widthSrcSet ??
          getCloudinaryDprSrcSet({ imageKey: props.imageKey, prefix: props.prefix, type: props.type })
        }
        sizes={widthSrcSet ? props.sizes : undefined}
      />

      <img
        alt={altText}
        title={altText}
        src={url(imageKey, 'jpg', props.type)}
        className={props.className}
        srcSet={`${url(imageKey, 'jpg', props.type, '@2x')} 2x`}
        style={props.style}
        loading={props.priority ? 'eager' : props.loading}
        fetchpriority={props.priority ? 'high' : undefined}
      />
    </picture>
  );
};

CloudinaryPicture.propTypes = {
  prefix: PropTypes.string,
  imageKey: PropTypes.string,
  // Overrides the generated alt catalog. Pass "" for decorative images.
  alt: PropTypes.string,
  assetType: PropTypes.oneOf(['image', 'video']),
  type: PropTypes.oneOf(['big', 'small']),
  // Opt-in width-descriptor srcset; must match the folder's CDN ladder (see WIDTH_LADDERS)
  // and always come together with `sizes`. Mutually exclusive with `type`.
  widths: PropTypes.arrayOf(PropTypes.number),
  sizes: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  // Set on the LCP image only — adds fetchpriority="high" + loading="eager".
  priority: PropTypes.bool,
  loading: PropTypes.oneOf(['eager', 'lazy']),
  // Video only: loop the clip, and forward the <video> node to the caller (e.g. the carousel
  // drives playback to hold auto-advance until the video ends).
  loop: PropTypes.bool,
  videoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

CloudinaryPicture.defaultProps = {
  prefix: '',
  assetType: 'image',
  loop: true,
};

export default CloudinaryPicture;
